import React, { useState, useEffect } from 'react';
import { styles } from './adminStyles';

// Manual Bid Override & Winner Promotion (post-closure contract edge cases).
// One modal, two modes:
//  - 'withdraw': mark a currently-accepted (winning) bid as withdrawn, payout -> £0.
//  - 'promote': mark a standby (rejected/explore) bid as accepted, at either its
//    original price or a custom amount capped to live budget/cap headroom
//    (fetched from GET /admin/bids/:id/promotion-headroom on open).
// Both require a reason, which is stored on the bid's manualOverrideHistory and
// emailed to the farmer.
const AdminBidOverrideModal = ({ mode, bid, auctionId, onClose, onWithdrawWinner, onPromoteBid, fetchPromotionHeadroom }) => {
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const [headroom, setHeadroom] = useState(null);
    const [headroomLoading, setHeadroomLoading] = useState(mode === 'promote');
    const [priceChoice, setPriceChoice] = useState('original'); // 'original' | 'custom'
    const [customAmount, setCustomAmount] = useState('');

    useEffect(() => {
        if (mode !== 'promote') return;
        let cancelled = false;
        setHeadroomLoading(true);
        fetchPromotionHeadroom(bid._id)
            .then(data => { if (!cancelled) setHeadroom(data); })
            .catch(err => { if (!cancelled) setError(err.response?.data?.msg || 'Failed to load budget headroom.'); })
            .finally(() => { if (!cancelled) setHeadroomLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, bid._id]);

    const originalAmount = bid.bidPrice ?? bid.amount ?? 0;
    const fitsAtOriginalPrice = headroom ? originalAmount <= headroom.maxPromotableAmount : null;

    const handleSubmit = async () => {
        if (!reason.trim()) {
            setError('A reason is required.');
            return;
        }
        setError('');
        setIsSubmitting(true);
        try {
            if (mode === 'withdraw') {
                await onWithdrawWinner(bid._id, reason.trim(), auctionId);
            } else {
                const payoutAmount = priceChoice === 'custom' ? parseFloat(customAmount) : undefined;
                if (priceChoice === 'custom' && (!customAmount || isNaN(payoutAmount) || payoutAmount <= 0)) {
                    setError('Enter a valid custom payout amount.');
                    setIsSubmitting(false);
                    return;
                }
                await onPromoteBid(bid._id, reason.trim(), payoutAmount, auctionId);
            }
            onClose();
        } catch (err) {
            // Parent handler already alert()s the specific server message —
            // keep the modal open so the admin can adjust the amount and retry.
        } finally {
            setIsSubmitting(false);
        }
    };

    const isWithdraw = mode === 'withdraw';
    const title = isWithdraw ? 'Withdraw Winning Bid' : 'Promote to Winning Bid';
    const accentColor = isWithdraw ? '#F59E0B' : '#0D9488';

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={{ ...styles.modal, width: '520px', height: 'auto', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
                    <h2 style={{ margin: 0, ...styles.h2, color: accentColor }}>{title}</h2>
                    <button onClick={onClose} style={{ ...styles.btnReversed, padding: '8px 16px' }}>Close ✕</button>
                </div>

                <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '15px', fontSize: '0.9rem', color: '#0F172A', fontFamily: '"Inter", sans-serif' }}>
                        <strong>{bid.user?.name || 'Unknown Farmer'}</strong> — {bid.actionName || 'Unknown Action'}
                        <div style={{ color: '#475569', fontSize: '0.85rem' }}>{bid.parcel ? (bid.parcel.parcelRef || bid.parcel.parcelId) : 'Whole Farm'}</div>
                    </div>

                    {isWithdraw ? (
                        <p style={{ fontSize: '0.85rem', color: '#475569', fontFamily: '"Inter", sans-serif' }}>
                            This will mark the bid as withdrawn and set its payout to £0, freeing the budget/cap headroom it was using for a standby bid to be promoted into. The farmer will be notified by email.
                        </p>
                    ) : (
                        <>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={styles.label}>Budget Headroom</label>
                                {headroomLoading ? (
                                    <p style={styles.textMuted}>Loading current headroom...</p>
                                ) : headroom ? (
                                    <div style={{ fontSize: '0.85rem', color: '#0F172A', fontFamily: '"Inter", sans-serif', backgroundColor: '#F8FAFC', border: '1px solid #eee', borderRadius: '4px', padding: '10px 12px' }}>
                                        Maximum promotable amount right now: <strong>£{Number(headroom.maxPromotableAmount).toLocaleString('en-GB')}</strong>
                                    </div>
                                ) : null}
                            </div>

                            <label style={styles.label}>Payout Amount</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif', opacity: fitsAtOriginalPrice === false ? 0.5 : 1 }}>
                                    <input
                                        type="radio"
                                        checked={priceChoice === 'original'}
                                        disabled={fitsAtOriginalPrice === false}
                                        onChange={() => setPriceChoice('original')}
                                    />
                                    Accept at Original Price (£{originalAmount.toLocaleString('en-GB')})
                                    {fitsAtOriginalPrice === false && <span style={{ color: '#F59E0B' }}> — exceeds available headroom</span>}
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif' }}>
                                    <input
                                        type="radio"
                                        checked={priceChoice === 'custom'}
                                        onChange={() => setPriceChoice('custom')}
                                    />
                                    Custom Capped Amount
                                </label>
                                {priceChoice === 'custom' && (
                                    <input
                                        type="number"
                                        min="0"
                                        max={headroom?.maxPromotableAmount}
                                        step="0.01"
                                        value={customAmount}
                                        onChange={(e) => setCustomAmount(e.target.value)}
                                        placeholder={headroom ? `Up to £${Number(headroom.maxPromotableAmount).toLocaleString('en-GB')}` : ''}
                                        style={{ ...styles.input, marginBottom: 0 }}
                                    />
                                )}
                            </div>
                        </>
                    )}

                    <label style={styles.label}>Reason (required — sent to the farmer)</label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Explain why this override is being made..."
                        style={{ ...styles.input, height: '70px', resize: 'vertical' }}
                    />

                    {error && <p style={{ color: '#DC2626', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif' }}>{error}</p>}

                    <button
                        type="button"
                        disabled={isSubmitting || (mode === 'promote' && headroomLoading)}
                        onClick={handleSubmit}
                        style={{ ...styles.btnPrimary, width: '100%', backgroundColor: accentColor, opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                    >
                        {isSubmitting ? 'Saving...' : (isWithdraw ? 'Confirm Withdrawal' : 'Confirm Promotion')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminBidOverrideModal;
